import {valueSchema, answerSchema} from './schema.js'
import {validate} from '../../../lib/schema-validator.js'

export const DATA_POINT_ID = 'energy_sources_pefcr'

export const dataPointDefinition = {
  retracedId: 'energy_sources_pefcr',
  name: 'What are your energy sources and how much was consumed?',
  meta: {
    isMultipleRow: true,
    columns: [
      {
        key: 1,
        name: 'Consumption Category',
        type: 'SELECT',
        id: 'consumption_category',
        options: [
          {
            key: 'COOLING',
            text: 'Cooling',
          },
          {
            key: 'ELECTRICITY',
            text: 'Electricity',
          },
          {
            key: 'HEATING',
            text: 'Heating',
          },
          {
            key: 'STEAM',
            text: 'Steam',
          },
        ],
      },
      {
        key: 2,
        name: 'Energy Sources',
        type: 'SELECT',
        id: 'energy_sources',
        options: [
          {
            key: 'CHILLED_WATER',
            text: 'Chilled Water',
          },
          {
            key: 'COAL',
            text: 'Coal',
          },
          {
            key: 'DIESEL',
            text: 'Diesel',
          },
          {
            key: 'EXHAUST_HEAT',
            text: 'Exhaust Heat',
          },
          {
            key: 'FUEL_OIL',
            text: 'Fuel Oil',
          },
          {
            key: 'GEOTHERMAL',
            text: 'Geothermal',
          },
          {
            key: 'HYDRO',
            text: 'Hydro',
          },
          {
            key: 'LIQUEFIED_PETROLEUM_GAS',
            text: 'Liquefied Petroleum Gas',
          },
          {
            key: 'NATURAL_GAS',
            text: 'Natural Gas',
          },
          {
            key: 'NUCLEAR',
            text: 'Nuclear',
          },
          {
            key: 'PETROL',
            text: 'Petrol',
          },
          {
            key: 'PRIMARY_BIOMASS',
            text: 'Primary Biomass',
          },
          {
            key: 'SECONDARY_BIOMASS',
            text: 'Secondary Biomass',
          },
          {
            key: 'SOLAR_PHOTOVOLTAIC',
            text: 'Solar Photovoltaic',
          },
          {
            key: 'TIDAL',
            text: 'Tidal',
          },
          {
            key: 'UNKNOWN',
            text: 'Unknown',
          },
          {
            key: 'WIND',
            text: 'Wind',
          },
          {
            key: 'OTHER_RENEWABLE',
            text: 'Other Renewable',
          },
        ],
      },
      {
        key: 3,
        name: 'Total Annual Consumption',
        type: 'NUMBER_DYNAMIC_UOM',
        id: 'total_annual_consumption',
        unitOptions: [
          {
            label: 'm³',
            text: 'm³',
            key: 'CUBIC_METRE',
          },
          {
            label: 'l',
            text: 'Litre',
            key: 'LITRE',
          },
          {
            label: 'kWh',
            text: 'kWh',
            key: 'KILOWATT_HOUR',
          },
        ],
      },
      {
        key: 4,
        name: 'Year',
        id: 'year',
        type: 'SELECT_YEAR',
      },
    ],
  },
  fieldTypeId: 'TABLE',
  type: 'RELATIONAL',
  options: [
    {
      consumptionCategory: [
        {
          key: 'COOLING',
          text: 'Cooling',
        },
        {
          key: 'ELECTRICITY',
          text: 'Electricity',
        },
        {
          key: 'HEATING',
          text: 'Heating',
        },
        {
          key: 'STEAM',
          text: 'Steam',
        },
      ],
    },
    {
      energySources: [
        {
          key: 'CHILLED_WATER',
          text: 'Chilled Water',
        },
        {
          key: 'COAL',
          text: 'Coal',
        },
        {
          key: 'DIESEL',
          text: 'Diesel',
        },
        {
          key: 'EXHAUST_HEAT',
          text: 'Exhaust Heat',
        },
        {
          key: 'FUEL_OIL',
          text: 'Fuel Oil',
        },
        {
          key: 'GEOTHERMAL',
          text: 'Geothermal',
        },
        {
          key: 'HYDRO',
          text: 'Hydro',
        },
        {
          key: 'LIQUEFIED_PETROLEUM_GAS',
          text: 'Liquefied Petroleum Gas',
        },
        {
          key: 'NATURAL_GAS',
          text: 'Natural Gas',
        },
        {
          key: 'NUCLEAR',
          text: 'Nuclear',
        },
        {
          key: 'PETROL',
          text: 'Petrol',
        },
        {
          key: 'PRIMARY_BIOMASS',
          text: 'Primary Biomass',
        },
        {
          key: 'SECONDARY_BIOMASS',
          text: 'Secondary Biomass',
        },
        {
          key: 'SOLAR_PHOTOVOLTAIC',
          text: 'Solar Photovoltaic',
        },
        {
          key: 'TIDAL',
          text: 'Tidal',
        },
        {
          key: 'UNKNOWN',
          text: 'Unknown',
        },
        {
          key: 'WIND',
          text: 'Wind',
        },
      ],
    },
    {
      unit: [
        {
          text: 'm³',
          key: 'CUBIC_METRE',
        },
        {
          text: 'Litre',
          key: 'LITRE',
        },
        {
          text: 'kWh',
          key: 'KILOWATT_HOUR',
        },
      ],
    },
  ],
  schema: {
    type: 'object',
    properties: {
      value: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            consumptionCategory: {
              type: 'string',
              enum: ['COOLING', 'ELECTRICITY', 'HEATING', 'STEAM'],
            },
            energySources: {
              type: 'string',
              enum: [
                'CHILLED_WATER',
                'COAL',
                'DIESEL',
                'EXHAUST_HEAT',
                'FUEL_OIL',
                'GEOTHERMAL',
                'HYDRO',
                'LIQUEFIED_PETROLEUM_GAS',
                'NATURAL_GAS',
                'NUCLEAR',
                'PETROL',
                'PRIMARY_BIOMASS',
                'SECONDARY_BIOMASS',
                'SOLAR_PHOTOVOLTAIC',
                'TIDAL',
                'UNKNOWN',
                'WIND',
              ],
            },
            unit: {
              type: 'string',
              enum: ['CUBIC_METRE', 'LITRE', 'KILOWATT_HOUR'],
            },
            totalAnnualConsumption: {
              type: 'number',
            },
            year: {
              type: 'number',
              minimum: 1000,
              maximum: 9000,
            },
          },
          required: ['consumptionCategory', 'energySources', 'year', 'unit', 'totalAnnualConsumption'],
        },
      },
    },
    required: ['value'],
  },
  externalIds: [],
  sortingId: 640,
  sectionId: '2.1.6.1',
  impactArea: 'Energy sources',
  impactAreaId: 'ENERGY_SOURCES',
}

export const validateSchema = data => {
  try {
    validate({value: data, schema: valueSchema})
    return data
  } catch (error) {
    throw new Error(`${DATA_POINT_ID}: ${error}`)
  }
}

export const transformAnswer = (answer, {meta}) => {
  try {
    validate({value: answer, schema: answerSchema})
    const formattedDataPointKey = DATA_POINT_ID.replace(/_([a-z])/g, g => g[1].toUpperCase())
    return {[formattedDataPointKey]: answer}
  } catch (error) {
    throw new Error(`${DATA_POINT_ID}: ${error}`)
  }
}
